import { useEffect, useRef, useState } from "react";

interface AnimatedCounterProps {
  /** The string value to animate, e.g. "500+", "10K+", "50+" */
  value: string;
  /** Duration of the animation in ms */
  duration?: number;
  className?: string;
}

/**
 * Parses a stat string like "500+", "10K+", "1.2K+" into its numeric value,
 * suffix (e.g. "K+", "+"), and whether it uses a multiplier.
 */
function parseStatValue(value: string): { number: number; suffix: string; hasMultiplier: boolean } {
  const match = value.match(/^([\d,.]+)\s*(K\+?|M\+?|\+?)$/i);
  if (!match) {
    return { number: 0, suffix: value, hasMultiplier: false };
  }

  const numStr = match[1].replace(/,/g, "");
  const suffix = match[2] || "";
  const num = parseFloat(numStr);

  return {
    number: num,
    suffix,
    hasMultiplier: /[KkMm]/.test(suffix),
  };
}

/**
 * Easing function for a smooth deceleration effect.
 */
function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/**
 * AnimatedCounter displays a number that counts up from 0 to the target value
 * when it scrolls into view, with a smooth easing animation.
 */
const AnimatedCounter = ({ value, duration = 2000, className }: AnimatedCounterProps) => {
  const [displayValue, setDisplayValue] = useState("0");
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          startAnimation();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnimated]);

  const startAnimation = () => {
    const { number: target, suffix, hasMultiplier } = parseStatValue(value);

    if (target === 0) {
      setDisplayValue(value);
      return;
    }

    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutExpo(progress);

      const currentValue = easedProgress * target;

      // Format the number appropriately
      let formatted: string;
      if (hasMultiplier) {
        // For values like "10K+", show decimal during animation
        if (currentValue < 1) {
          formatted = currentValue.toFixed(1);
        } else {
          formatted = currentValue >= 10
            ? Math.round(currentValue).toString()
            : currentValue.toFixed(1);
        }
      } else {
        formatted = Math.round(currentValue).toLocaleString();
      }

      setDisplayValue(formatted + suffix);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Ensure final value matches exactly
        setDisplayValue(value);
      }
    };

    requestAnimationFrame(animate);
  };

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  );
};

export default AnimatedCounter;
